import React, { useState } from 'react';
import { Paper, Box, TextField, Grid, Typography, Button, Card, CardContent, IconButton, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const FooterTab = () => {
  const { t } = useTranslation();
  const { config, handleChange, handleAddArrayItem, handleArrayChange, handleRemoveArrayItem, isFieldConfigurable, isFieldFixed, loadConfig, getFieldTypeValues } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  const getLabel = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const footerPath = 'siteConfig.footer';
  const socialLinksPath = 'siteConfig.footer.socialLinks';
  const isSocialLinksConfigurable = isFieldConfigurable(socialLinksPath);
  const isSocialLinksFixed = isFieldFixed(socialLinksPath);

  const platformTypeValuesFromMetadata =
    getFieldTypeValues?.('siteConfig.footer.socialLinks.platform') ||
    getFieldTypeValues?.('siteConfig.footer.socialLinks[].platform');
  const platformTypeValues = platformTypeValuesFromMetadata?.length
    ? platformTypeValuesFromMetadata
    : ['instagram', 'facebook', 'tiktok'];

  const hasEditableFields = isSocialLinksConfigurable && !isSocialLinksFixed;
  
  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updates = {};
      
      if (isSocialLinksConfigurable && !isSocialLinksFixed) {
        updates[socialLinksPath] = config.siteConfig.footer.socialLinks;
      }

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving footer:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Grid container spacing={2}>
        {isSocialLinksConfigurable && !isSocialLinksFixed && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddArrayItem('siteConfig.footer.socialLinks', { platform: platformTypeValues.length > 0 ? platformTypeValues[0] : '', url: '' })}
                variant="outlined"
              >
                {getLabel('add_social_link', 'Agregar Red Social')}
              </Button>
            </Box>
            {config.siteConfig.footer.socialLinks?.map((link, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle1">{getLabel('social_link', 'Red Social')} {index + 1}</Typography>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('siteConfig.footer.socialLinks', index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>{getLabel('platform', 'Plataforma')}</InputLabel>
                        <Select
                          value={link.platform || ''}
                          onChange={(e) => handleArrayChange('siteConfig.footer.socialLinks', index, 'platform', e.target.value)}
                          label={getLabel('platform', 'Plataforma')}
                        >
                          {platformTypeValues.length > 0 ? (
                            platformTypeValues.map(platform => (
                              <MenuItem key={platform} value={platform}>
                                {getLabel(platform, platform)}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              {getLabel('no_options_available', 'No hay opciones disponibles')}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={getLabel('url', 'URL')}
                        value={link.url || ''}
                        onChange={(e) => handleArrayChange('siteConfig.footer.socialLinks', index, 'url', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default FooterTab;
